import type { BusinessAnalysisAoi, BusinessAnalysisRadiusResult, BusinessAnalysisSnapshot } from "../../shared/types";

const scoreDefinitions = [
  ["growth", "Growth", 10, "percent", "Average five-year population and household growth"],
  ["residentialMarket", "Residential market size", 15, "number", "Resident population within the drive-time trade area"],
  ["daytimeMarket", "Daytime market size", 10, "number", "Daytime population at work and its share of daytime activity"],
  ["ageProfile", "Age profile fit", 5, "number", "Similarity to the age profile of operating stations"],
  ["food", "Food", 10, "currency", "Annual household food expenditure potential"],
  ["snacksBeverage", "Snacks & Beverage", 10, "currency", "Annual snacks and beverage expenditure potential"],
  ["tobacco", "Vapes / tobacco products", 5, "currency", "Annual tobacco-products expenditure potential used for the Vapes category"],
  ["cigarettes", "Cigarettes", 5, "currency", "Annual cigarette expenditure potential used for the Tobacco category"],
  ["cannabis", "Cannabis", 5, "currency", "Annual cannabis expenditure potential"],
  ["fuel", "Fuel", 25, "currency", "Annual household fuel expenditure potential"]
] as const;

const categories = ["Food", "Snacks & Beverage", "Tobacco", "Cigarettes", "Cannabis", "Fuel"] as const;

function result(radiusMinutes: 5 | 10 | 20, score: number, station: string, factors: number[], scale: number): BusinessAnalysisRadiusResult {
  const radiusKey = `R${String(radiusMinutes).padStart(2, "0")}` as BusinessAnalysisRadiusResult["radiusKey"];
  const stationFitScore = Math.max(48, Math.min(94, score + 7));
  const benchmarkValues = [0.028, 5100 * scale, 3700 * scale, 41.2, 21_500_000 * scale, 680_000 * scale, 4_200_000 * scale, 3_900_000 * scale, 1_800_000 * scale, 8_700_000 * scale];
  const aoiValues = [0.041, 6400 * scale, 4300 * scale, 39.8, 28_380_000 * scale, 802_400 * scale, 4_452_000 * scale, 4_017_000 * scale, 2_232_000 * scale, 11_223_000 * scale];
  const benchmarkScores = [58, 63, 55, 72, 61, 68, 57, 59, 54, 65];
  return {
    radiusKey,
    radiusMinutes,
    suitabilityScore: score,
    opportunityScore: Math.round((score * 4 - stationFitScore) / 3),
    stationFitScore,
    grade: score >= 80 ? "Excellent" : score >= 65 ? "Strong" : score >= 50 ? "Moderate" : "Caution",
    comparableStation: { poiSk: station.includes("Cranbrook") ? "30904" : "30901", name: station, city: "Benchmark city", province: "Ontario", similarityScore: stationFitScore },
    scores: scoreDefinitions.map(([key, label, weight, unit, description], index) => ({
      key, label, weight, unit, description,
      score: factors[index], benchmarkScore: benchmarkScores[index],
      value: aoiValues[index], benchmarkValue: benchmarkValues[index]
    })),
    categories: categories.map((category, index) => {
      const benchmarkExpenditure = [21_500_000, 680_000, 4_200_000, 3_900_000, 1_800_000, 8_700_000][index] * scale;
      const categoryIndex = [132, 118, 106, 103, 124, 129][index] + (score - 70);
      return { category, benchmarkExpenditure, aoiExpenditure: benchmarkExpenditure * categoryIndex / 100, index: categoryIndex };
    }),
    highlights: ["Market size is above the operating-station benchmark.", "Fuel expenditure potential is strong versus the comparable station."],
    risks: score < 65 ? ["Growth is below the preferred development threshold."] : []
  };
}

function aoi(aoiSk: string, name: string, score: number, factors: number[]): BusinessAnalysisAoi {
  return {
    aoiSk, name, city: name, province: name === "Cranbrook" ? "British Columbia" : "Manitoba",
    overallScore: score,
    recommendation: score >= 80 ? "Prioritize" : score >= 65 ? "Advance with validation" : score >= 50 ? "Monitor" : "Do not prioritize",
    results: [
      result(5, Math.max(35, score - 6), "Jocko Point Gen 7", factors.map((value) => Math.max(20, value - 8)), 0.12),
      result(10, score, "Sarnia Gen 7", factors, 0.5),
      result(20, Math.min(94, score + 5), "Cranbrook Comparable Station", factors.map((value) => Math.min(96, value + 6)), 1)
    ]
  };
}

export const mockBusinessAnalysis: BusinessAnalysisSnapshot = {
  asOfDate: new Date().toISOString(), methodologyVersion: "BA-2.0", aoiCount: 3, stationCount: 11,
  aois: [
    aoi("62515", "Cranbrook", 82, [84, 91, 86, 72, 88, 90, 74, 77, 82, 93]),
    aoi("62514", "Opaskwayak", 67, [42, 78, 69, 80, 66, 71, 62, 64, 58, 82]),
    aoi("62511", "AWZ", 54, [51, 38, 34, 74, 48, 45, 52, 50, 43, 57])
  ],
  salesBenchmarks: [
    { stationSk: "30904", stationName: "Sarnia Gen 7", category: "Fuel", actualSales: 10_900_000, esriExpenditure: 8_700_000, realizationRate: 125.3 },
    { stationSk: "30904", stationName: "Sarnia Gen 7", category: "Convenience", actualSales: 4_850_000, esriExpenditure: 4_120_000, realizationRate: 117.7 },
    { stationSk: "30901", stationName: "Jocko Point Gen 7", category: "Fuel", actualSales: 7_200_000, esriExpenditure: 6_900_000, realizationRate: 104.3 }
  ]
};
